import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Settings, Save, Loader2, FileText, AlertCircle, CheckCircle, Mail, Plus, X, Bell, Ticket } from 'lucide-react';
import api, { adminQuotesAPI } from '../../services/api';
import QuoteLayoutSettingsCard from '../../components/admin/QuoteLayoutSettingsCard';

export default function QuoteSettingsPage() {
  const queryClient = useQueryClient();
  const [terms, setTerms] = useState('');
  const [hasTermsChanges, setHasTermsChanges] = useState(false);
  const [termsSuccess, setTermsSuccess] = useState(false);

  // Notification settings state
  const [notificationSettings, setNotificationSettings] = useState({
    sendToInternal: true,
    sendToCustomer: true,
    internalRecipients: [],
  });
  const [hasNotificationChanges, setHasNotificationChanges] = useState(false);
  const [notificationSuccess, setNotificationSuccess] = useState(false);
  const [newRecipient, setNewRecipient] = useState('');

  // Ticket settings state
  const [ticketSettings, setTicketSettings] = useState({
    enabled: false,
    boardId: null,
    statusId: null,
  });
  const [hasTicketChanges, setHasTicketChanges] = useState(false);
  const [ticketSuccess, setTicketSuccess] = useState(false);

  // Fetch current terms
  const { data: termsData, isLoading: termsLoading, error: termsError } = useQuery({
    queryKey: ['quote-terms'],
    queryFn: () => api.get('/admin/quotes/terms'),
  });

  // Fetch notification settings
  const { data: notifData, isLoading: notifLoading, error: notifError } = useQuery({
    queryKey: ['quote-notification-settings'],
    queryFn: () => api.get('/admin/quotes/notification-settings'),
  });

  // Fetch ticket settings
  const { data: ticketData, isLoading: ticketLoading, error: ticketError } = useQuery({
    queryKey: ['quote-ticket-settings'],
    queryFn: () => adminQuotesAPI.getTicketSettings(),
  });

  // Fetch service boards for dropdown
  const { data: boardsData } = useQuery({
    queryKey: ['service-boards'],
    queryFn: () => adminQuotesAPI.getServiceBoards(),
  });

  // Fetch statuses for selected board
  const { data: statusesData } = useQuery({
    // Not ['board-statuses', id]: BoardRouteEditor caches a different endpoint's
    // shape under that key, and whichever page loaded second got the other's data.
    queryKey: ['quote-settings', 'board-statuses', ticketSettings.boardId],
    queryFn: () => adminQuotesAPI.getBoardStatuses(ticketSettings.boardId),
    enabled: !!ticketSettings.boardId,
  });

  // Update terms when data loads
  useEffect(() => {
    if (termsData?.data?.terms) {
      setTerms(termsData.data.terms);
      setHasTermsChanges(false);
    }
  }, [termsData]);

  // Update notification settings when data loads
  useEffect(() => {
    if (notifData?.data) {
      setNotificationSettings({
        sendToInternal: notifData.data.sendToInternal !== false,
        sendToCustomer: notifData.data.sendToCustomer !== false,
        internalRecipients: notifData.data.internalRecipients || [],
      });
      setHasNotificationChanges(false);
    }
  }, [notifData]);

  // Update ticket settings when data loads
  useEffect(() => {
    if (ticketData?.data) {
      setTicketSettings({
        enabled: ticketData.data.enabled || false,
        boardId: ticketData.data.boardId || null,
        statusId: ticketData.data.statusId || null,
      });
      setHasTicketChanges(false);
    }
  }, [ticketData]);

  // Save terms mutation
  const saveTermsMutation = useMutation({
    mutationFn: (newTerms) => api.put('/admin/quotes/terms', { terms: newTerms }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['quote-terms'] });
      setHasTermsChanges(false);
      setTermsSuccess(true);
      setTimeout(() => setTermsSuccess(false), 3000);
    },
  });

  // Save notification settings mutation
  const saveNotificationMutation = useMutation({
    mutationFn: (settings) => api.put('/admin/quotes/notification-settings', settings),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['quote-notification-settings'] });
      setHasNotificationChanges(false);
      setNotificationSuccess(true);
      setTimeout(() => setNotificationSuccess(false), 3000);
    },
  });

  // Save ticket settings mutation
  const saveTicketMutation = useMutation({
    mutationFn: (settings) => adminQuotesAPI.updateTicketSettings(settings),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['quote-ticket-settings'] });
      setHasTicketChanges(false);
      setTicketSuccess(true);
      setTimeout(() => setTicketSuccess(false), 3000);
    },
  });

  const handleTermsChange = (value) => {
    setTerms(value);
    setHasTermsChanges(value !== termsData?.data?.terms);
  };

  const handleSaveTerms = () => {
    saveTermsMutation.mutate(terms);
  };

  const handleNotificationSettingChange = (key, value) => {
    setNotificationSettings((prev) => ({ ...prev, [key]: value }));
    setHasNotificationChanges(true);
  };

  const handleAddRecipient = () => {
    const email = newRecipient.trim().toLowerCase();
    if (email && email.includes('@') && !notificationSettings.internalRecipients.includes(email)) {
      setNotificationSettings((prev) => ({
        ...prev,
        internalRecipients: [...prev.internalRecipients, email],
      }));
      setNewRecipient('');
      setHasNotificationChanges(true);
    }
  };

  const handleRemoveRecipient = (email) => {
    setNotificationSettings((prev) => ({
      ...prev,
      internalRecipients: prev.internalRecipients.filter((e) => e !== email),
    }));
    setHasNotificationChanges(true);
  };

  const handleSaveNotifications = () => {
    saveNotificationMutation.mutate(notificationSettings);
  };

  const handleTicketSettingChange = (key, value) => {
    setTicketSettings((prev) => {
      const updated = { ...prev, [key]: value };
      // Reset statusId if board changes
      if (key === 'boardId') {
        updated.statusId = null;
      }
      return updated;
    });
    setHasTicketChanges(true);
  };

  const handleSaveTicketSettings = () => {
    saveTicketMutation.mutate(ticketSettings);
  };

  const isLoading = termsLoading || notifLoading || ticketLoading;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary-500" />
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center gap-3 mb-2">
          <Settings className="w-6 h-6 text-neutral-400" />
          <h1 className="text-2xl font-bold text-neutral-900 dark:text-white">Quote Settings</h1>
        </div>
        <p className="text-neutral-500 dark:text-neutral-400">
          Configure default settings for quotes shared via link
        </p>
      </div>

      <QuoteLayoutSettingsCard />

      {/* Notification Settings */}
      <div className="bg-white dark:bg-neutral-800 rounded-lg border border-neutral-200 dark:border-neutral-700">
        <div className="px-6 py-4 border-b border-neutral-200 dark:border-neutral-700">
          <div className="flex items-center gap-2">
            <Bell className="w-5 h-5 text-neutral-400" />
            <h2 className="text-lg font-semibold text-neutral-900 dark:text-white">
              Email Notifications
            </h2>
          </div>
          <p className="text-sm text-neutral-500 dark:text-neutral-400 mt-1">
            Configure who receives email notifications when quotes are approved or rejected.
          </p>
        </div>

        <div className="p-6 space-y-6">
          {notifError && (
            <div className="p-3 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-lg flex items-center gap-2 text-red-700 dark:text-red-300">
              <AlertCircle className="w-4 h-4 flex-shrink-0" />
              Failed to load notification settings: {notifError.message}
            </div>
          )}

          {/* Send to Internal Staff Toggle */}
          <div className="flex items-center justify-between">
            <div>
              <h3 className="font-medium text-neutral-900 dark:text-white">Send to Internal Staff</h3>
              <p className="text-sm text-neutral-500 dark:text-neutral-400">
                Notify internal team members when quotes are approved or rejected
              </p>
            </div>
            <button
              onClick={() => handleNotificationSettingChange('sendToInternal', !notificationSettings.sendToInternal)}
              className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 ${
                notificationSettings.sendToInternal ? 'bg-primary-600' : 'bg-neutral-200 dark:bg-neutral-700'
              }`}
            >
              <span
                className={`inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                  notificationSettings.sendToInternal ? 'translate-x-5' : 'translate-x-0'
                }`}
              />
            </button>
          </div>

          {/* Internal Recipients */}
          {notificationSettings.sendToInternal && (
            <div className="pl-4 border-l-2 border-neutral-200 dark:border-neutral-700">
              <h4 className="text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
                Internal Recipients
              </h4>
              <div className="flex flex-wrap gap-2 mb-3">
                {notificationSettings.internalRecipients.map((email) => (
                  <span
                    key={email}
                    className="inline-flex items-center gap-1 px-3 py-1 bg-neutral-100 dark:bg-neutral-700 rounded-full text-sm"
                  >
                    <Mail className="w-3 h-3 text-neutral-400" />
                    <span className="text-neutral-700 dark:text-neutral-300">{email}</span>
                    <button
                      onClick={() => handleRemoveRecipient(email)}
                      className="ml-1 text-neutral-400 hover:text-red-500"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                ))}
                {notificationSettings.internalRecipients.length === 0 && (
                  <span className="text-sm text-neutral-500 dark:text-neutral-400">
                    No recipients configured - add email addresses below
                  </span>
                )}
              </div>
              <div className="flex gap-2">
                <input
                  type="email"
                  value={newRecipient}
                  onChange={(e) => setNewRecipient(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleAddRecipient()}
                  placeholder="email@example.com"
                  className="flex-1 px-3 py-2 border border-neutral-300 dark:border-neutral-600 bg-white dark:bg-neutral-700 text-neutral-900 dark:text-white rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                />
                <button
                  onClick={handleAddRecipient}
                  disabled={!newRecipient.trim() || !newRecipient.includes('@')}
                  className="flex items-center gap-1 px-3 py-2 bg-neutral-100 dark:bg-neutral-700 text-neutral-700 dark:text-neutral-300 rounded-lg hover:bg-neutral-200 dark:hover:bg-neutral-600 disabled:opacity-50"
                >
                  <Plus className="w-4 h-4" />
                  Add
                </button>
              </div>
            </div>
          )}

          {/* Send to Customer Toggle */}
          <div className="flex items-center justify-between pt-4 border-t border-neutral-200 dark:border-neutral-700">
            <div>
              <h3 className="font-medium text-neutral-900 dark:text-white">Send to Customer</h3>
              <p className="text-sm text-neutral-500 dark:text-neutral-400">
                Send a confirmation email to the customer who approved/rejected the quote
              </p>
            </div>
            <button
              onClick={() => handleNotificationSettingChange('sendToCustomer', !notificationSettings.sendToCustomer)}
              className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 ${
                notificationSettings.sendToCustomer ? 'bg-primary-600' : 'bg-neutral-200 dark:bg-neutral-700'
              }`}
            >
              <span
                className={`inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                  notificationSettings.sendToCustomer ? 'translate-x-5' : 'translate-x-0'
                }`}
              />
            </button>
          </div>

          {/* Save Button */}
          <div className="flex items-center justify-between pt-4 border-t border-neutral-200 dark:border-neutral-700">
            <div className="flex items-center gap-2">
              {notificationSuccess && (
                <span className="flex items-center gap-1 text-green-600 dark:text-green-400 text-sm">
                  <CheckCircle className="w-4 h-4" />
                  Saved successfully
                </span>
              )}
              {saveNotificationMutation.isError && (
                <span className="flex items-center gap-1 text-red-600 dark:text-red-400 text-sm">
                  <AlertCircle className="w-4 h-4" />
                  Failed to save
                </span>
              )}
            </div>
            <button
              onClick={handleSaveNotifications}
              disabled={!hasNotificationChanges || saveNotificationMutation.isPending}
              className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {saveNotificationMutation.isPending ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Save className="w-4 h-4" />
              )}
              Save Notification Settings
            </button>
          </div>
        </div>
      </div>

      {/* Question Ticket Settings */}
      <div className="bg-white dark:bg-neutral-800 rounded-lg border border-neutral-200 dark:border-neutral-700">
        <div className="px-6 py-4 border-b border-neutral-200 dark:border-neutral-700">
          <div className="flex items-center gap-2">
            <Ticket className="w-5 h-5 text-neutral-400" />
            <h2 className="text-lg font-semibold text-neutral-900 dark:text-white">
              Customer Questions
            </h2>
          </div>
          <p className="text-sm text-neutral-500 dark:text-neutral-400 mt-1">
            When enabled, customer questions from shared quote links create ConnectWise tickets for tracking and response.
          </p>
        </div>

        <div className="p-6 space-y-6">
          {ticketError && (
            <div className="p-3 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-lg flex items-center gap-2 text-red-700 dark:text-red-300">
              <AlertCircle className="w-4 h-4 flex-shrink-0" />
              Failed to load ticket settings: {ticketError.message}
            </div>
          )}

          {/* Enable Ticket Creation Toggle */}
          <div className="flex items-center justify-between">
            <div>
              <h3 className="font-medium text-neutral-900 dark:text-white">Create Tickets for Questions</h3>
              <p className="text-sm text-neutral-500 dark:text-neutral-400">
                Automatically create a ConnectWise ticket when a customer asks a question
              </p>
            </div>
            <button
              onClick={() => handleTicketSettingChange('enabled', !ticketSettings.enabled)}
              className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 ${
                ticketSettings.enabled ? 'bg-primary-600' : 'bg-neutral-200 dark:bg-neutral-700'
              }`}
            >
              <span
                className={`inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                  ticketSettings.enabled ? 'translate-x-5' : 'translate-x-0'
                }`}
              />
            </button>
          </div>

          {/* Board and Status Selection */}
          {ticketSettings.enabled && (
            <div className="pl-4 border-l-2 border-neutral-200 dark:border-neutral-700 space-y-4">
              {/* Board Selection */}
              <div>
                <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
                  Service Board
                </label>
                <select
                  value={ticketSettings.boardId || ''}
                  onChange={(e) => handleTicketSettingChange('boardId', e.target.value ? parseInt(e.target.value) : null)}
                  className="w-full px-3 py-2 border border-neutral-300 dark:border-neutral-600 bg-white dark:bg-neutral-700 text-neutral-900 dark:text-white rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                >
                  <option value="">Select a board...</option>
                  {boardsData?.data?.boards?.map((board) => (
                    <option key={board.id} value={board.id}>
                      {board.name}
                    </option>
                  ))}
                </select>
                <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-1">
                  Tickets will be created on this board (e.g., Sales, Pre-Sales)
                </p>
              </div>

              {/* Status Selection */}
              {ticketSettings.boardId && (
                <div>
                  <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
                    Default Status
                  </label>
                  <select
                    value={ticketSettings.statusId || ''}
                    onChange={(e) => handleTicketSettingChange('statusId', e.target.value ? parseInt(e.target.value) : null)}
                    className="w-full px-3 py-2 border border-neutral-300 dark:border-neutral-600 bg-white dark:bg-neutral-700 text-neutral-900 dark:text-white rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  >
                    <option value="">Use board default</option>
                    {statusesData?.data?.statuses?.map((status) => (
                      <option key={status.id} value={status.id}>
                        {status.name}
                      </option>
                    ))}
                  </select>
                  <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-1">
                    Initial status for new question tickets
                  </p>
                </div>
              )}

              {!ticketSettings.boardId && (
                <div className="p-3 bg-yellow-50 dark:bg-yellow-900/30 border border-yellow-200 dark:border-yellow-800 rounded-lg flex items-center gap-2 text-yellow-700 dark:text-yellow-300 text-sm">
                  <AlertCircle className="w-4 h-4 flex-shrink-0" />
                  Please select a board to enable ticket creation
                </div>
              )}
            </div>
          )}

          {/* Save Button */}
          <div className="flex items-center justify-between pt-4 border-t border-neutral-200 dark:border-neutral-700">
            <div className="flex items-center gap-2">
              {ticketSuccess && (
                <span className="flex items-center gap-1 text-green-600 dark:text-green-400 text-sm">
                  <CheckCircle className="w-4 h-4" />
                  Saved successfully
                </span>
              )}
              {saveTicketMutation.isError && (
                <span className="flex items-center gap-1 text-red-600 dark:text-red-400 text-sm">
                  <AlertCircle className="w-4 h-4" />
                  Failed to save
                </span>
              )}
            </div>
            <button
              onClick={handleSaveTicketSettings}
              disabled={!hasTicketChanges || saveTicketMutation.isPending || (ticketSettings.enabled && !ticketSettings.boardId)}
              className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {saveTicketMutation.isPending ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Save className="w-4 h-4" />
              )}
              Save Ticket Settings
            </button>
          </div>
        </div>
      </div>

      {/* Terms and Conditions */}
      <div className="bg-white dark:bg-neutral-800 rounded-lg border border-neutral-200 dark:border-neutral-700">
        <div className="px-6 py-4 border-b border-neutral-200 dark:border-neutral-700">
          <div className="flex items-center gap-2">
            <FileText className="w-5 h-5 text-neutral-400" />
            <h2 className="text-lg font-semibold text-neutral-900 dark:text-white">
              Default Terms and Conditions
            </h2>
          </div>
          <p className="text-sm text-neutral-500 dark:text-neutral-400 mt-1">
            These terms will be shown on all shared quote links unless overridden for a specific quote.
          </p>
        </div>

        <div className="p-6">
          {termsError && (
            <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-lg flex items-center gap-2 text-red-700 dark:text-red-300">
              <AlertCircle className="w-4 h-4 flex-shrink-0" />
              Failed to load terms: {termsError.message}
            </div>
          )}

          <textarea
            value={terms}
            onChange={(e) => handleTermsChange(e.target.value)}
            rows={12}
            placeholder="Enter your default terms and conditions..."
            className="w-full px-4 py-3 border border-neutral-300 dark:border-neutral-600 bg-white dark:bg-neutral-700 text-neutral-900 dark:text-white rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 font-mono text-sm"
          />

          <p className="text-sm text-neutral-500 dark:text-neutral-400 mt-2">
            You can use line breaks to format the text. This will be displayed exactly as entered.
          </p>

          <div className="flex items-center justify-between mt-6 pt-4 border-t border-neutral-200 dark:border-neutral-700">
            <div className="flex items-center gap-2">
              {termsSuccess && (
                <span className="flex items-center gap-1 text-green-600 dark:text-green-400 text-sm">
                  <CheckCircle className="w-4 h-4" />
                  Saved successfully
                </span>
              )}
              {saveTermsMutation.isError && (
                <span className="flex items-center gap-1 text-red-600 dark:text-red-400 text-sm">
                  <AlertCircle className="w-4 h-4" />
                  Failed to save: {saveTermsMutation.error?.response?.data?.error || saveTermsMutation.error?.message}
                </span>
              )}
            </div>

            <button
              onClick={handleSaveTerms}
              disabled={!hasTermsChanges || saveTermsMutation.isPending}
              className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {saveTermsMutation.isPending ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Save className="w-4 h-4" />
              )}
              Save Changes
            </button>
          </div>
        </div>
      </div>

      {/* Preview */}
      <div className="bg-white dark:bg-neutral-800 rounded-lg border border-neutral-200 dark:border-neutral-700">
        <div className="px-6 py-4 border-b border-neutral-200 dark:border-neutral-700">
          <h2 className="text-lg font-semibold text-neutral-900 dark:text-white">Preview</h2>
          <p className="text-sm text-neutral-500 dark:text-neutral-400 mt-1">
            How the terms will appear on shared quote pages
          </p>
        </div>

        <div className="p-6">
          <div className="bg-neutral-50 dark:bg-neutral-700/50 rounded-lg p-4 prose prose-sm text-neutral-600 dark:text-neutral-300 whitespace-pre-wrap">
            {terms || 'No terms and conditions specified.'}
          </div>
        </div>
      </div>
    </div>
  );
}
